import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { PageError } from '@/components/ui/PageAsyncState'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { computeDemandListKpis } from '@/features/demand/demandListMetrics'
import { demandService, type DemandBatch } from '@/services/demand'

export function DemandListPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<DemandBatch[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      setRows(await demandService.list())
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Không tải được danh sách nhu cầu',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const kpis = useMemo(() => computeDemandListKpis(rows), [rows])

  const columns: ProtoColumn<DemandBatch>[] = useMemo(
    () => [
      {
        id: 'title',
        header: 'Đợt',
        sortable: true,
        hideable: false,
        width: 240,
        cell: (r) => <span className="font-medium">{r.title}</span>,
        sortValue: (r) => r.title,
      },
      {
        id: 'line_count',
        header: 'Số dòng',
        sortable: true,
        cell: (r) => r.line_count ?? 0,
        sortValue: (r) => r.line_count ?? 0,
      },
      {
        id: 'created_at',
        header: 'Tạo lúc',
        sortable: true,
        cell: (r) => new Date(r.created_at).toLocaleString('vi-VN'),
        sortValue: (r) => r.created_at,
      },
      {
        id: 'created_by',
        header: 'Người tạo',
        cell: (r) => r.created_by || '—',
        sortValue: (r) => r.created_by ?? '',
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Kế hoạch', to: '/planning' },
          { label: 'Nhu cầu vật tư' },
        ]}
      />
      <PageHeader
        title="Nhu cầu vật tư"
        subtitle="Đợt nhu cầu tay — Gross Explosion input"
        actions={
          <Button onClick={() => navigate('/purchasing/demand/new')}>
            <Plus className="h-4 w-4" />
            Tạo đợt mới
          </Button>
        }
      />

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Số đợt" value={kpis.batches} />
        <KpiCard label="Tổng dòng" value={kpis.lineCountSum} />
      </div>

      {error ? <PageError message={error} onRetry={() => void reload()} /> : null}

      <ProtoExcelTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        searchPlaceholder="Tìm đợt…"
        exportName="nhu-cau-demand"
        pageSize={50}
        loading={loading}
        emptyMessage="Chưa có đợt nhu cầu nào."
        showRowNumbers
        stickyLeftColumnIds={['title']}
        onRowClick={(r) => navigate(`/purchasing/demand/${r.id}`)}
      />
    </div>
  )
}
